// AI Elements `code-block` — adapted from the upstream shadcn registry
// template (sprint ai-elements-rendering). Deviations from upstream:
// - shiki removed: the upstream version injected hardcoded theme HTML
//   (one-light / one-dark-pro) via dangerouslySetInnerHTML, which
//   violates design-discipline (non-token colors) and ships a heavy
//   dep for a founder-first surface that rarely reads code. Rendering
//   is a plain <pre><code> on our tokens, same as the component it
//   replaces (components/CodeBlock.tsx, sprint code-block-render).
// - Clipboard write ports the textarea execCommand fallback from
//   components/CodeBlock.tsx (PWA / iOS Safari edge) and the 1500 ms
//   copied-reset, instead of failing when navigator.clipboard is
//   unavailable.
// - Header layout (language label + copy button, min-h-touch-target on
//   mobile) preserved from components/CodeBlock.tsx for visual parity;
//   the ≥44 px target survives per project-context.md §Desktop-first.

import { cn } from '@/lib/utils';
import { CheckIcon, CopyIcon } from 'lucide-react';
import {
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from '../../i18n';

const COPIED_RESET_MS = 1500;

export type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language?: string | null;
  showLineNumbers?: boolean;
};

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
});

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) => {
  const label = (language && language.length > 0 ? language : 'text').toLowerCase();
  const lines = showLineNumbers ? code.split('\n') : null;

  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          'my-2 w-full overflow-hidden rounded-md border border-border bg-card',
          className
        )}
        data-testid="code-block"
        {...props}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
          <span
            className="text-caption font-mono text-muted-foreground"
            data-testid="code-block-language"
          >
            {label}
          </span>
          {children ?? <CodeBlockCopyButton />}
        </div>
        <pre className="overflow-x-auto px-3 py-2 text-code-block font-mono text-foreground">
          {lines ? (
            <code>
              {lines.map((line, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static line list
                <span className="block" key={i}>
                  <span className="mr-4 inline-block min-w-10 select-none text-right text-muted-foreground">
                    {i + 1}
                  </span>
                  {line}
                </span>
              ))}
            </code>
          ) : (
            <code>{code}</code>
          )}
        </pre>
      </div>
    </CodeBlockContext.Provider>
  );
};

async function writeToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the textarea fallback (PWA / iOS Safari edge).
    }
  }
  if (typeof document === 'undefined') return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.left = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

export type CodeBlockCopyButtonProps = ComponentProps<'button'> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = COPIED_RESET_MS,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { code } = useContext(CodeBlockContext);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    const ok = await writeToClipboard(code);
    if (!ok) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }
    setCopied(true);
    onCopy?.();
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), timeout);
  };

  const Icon = copied ? CheckIcon : CopyIcon;

  return (
    <button
      type="button"
      onClick={handleCopy}
      // `min-h-touch-target md:min-h-0` enforces the ≥44px tap target on
      // mobile per `project-context.md` §Desktop-first (the ≥44 px target
      // survives the 2026-08-11 correction; the section it used to cite does not)
      // while keeping the compact Cursor-style chip on desktop.
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded px-1.5 py-0.5 text-caption text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150 min-h-touch-target md:min-h-0',
        className
      )}
      aria-label={
        copied
          ? t('aiElements.codeBlock.copiedAria')
          : t('aiElements.codeBlock.copyAria')
      }
      data-testid="code-block-copy"
      {...props}
    >
      {children ?? (
        <>
          <Icon aria-hidden size={12} />
          <span aria-live="polite">
            {copied
              ? t('aiElements.codeBlock.copied')
              : t('aiElements.codeBlock.copy')}
          </span>
        </>
      )}
    </button>
  );
};
